import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Logo from "../components/Logo";
import { LifeBuoy, CheckCircle2, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Self-service rescue page for paying customers stuck on the paywall.
 * Accepts their Parapet email + Stripe Customer ID + an admin token
 * (which a Parapet operator gives them by email when they reach out).
 *
 * Hits POST /api/billing/admin/rescue which doesn't need a Parapet login.
 */
export default function Unstuck() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [subscriptionId, setSubscriptionId] = useState("");
  const [token, setToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/api/billing/admin/rescue`,
        {
          email: email.trim().toLowerCase(),
          stripe_customer_id: customerId.trim() || undefined,
          stripe_subscription_id: subscriptionId.trim() || undefined,
        },
        { headers: { "X-Admin-Token": token.trim() } },
      );
      setResult({ ok: data.activated, data });
    } catch (err) {
      setResult({
        ok: false,
        data: { detail: err.response?.data?.detail || err.message },
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--parapet-bg)",
        color: "#111",
        display: "flex",
        flexDirection: "column",
      }}
      data-testid="unstuck-page"
    >
      <header
        style={{
          padding: "20px 40px",
          borderBottom: "1px solid var(--parapet-grid)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Logo />
        <button
          onClick={() => navigate("/login")}
          className="underline text-[12px]"
          style={{ color: "var(--parapet-text-secondary)" }}
          data-testid="unstuck-to-login"
        >
          Back to sign in
        </button>
      </header>

      <main style={{ flex: 1, display: "flex", justifyContent: "center", padding: "48px 24px" }}>
        <div style={{ maxWidth: 560, width: "100%" }}>
          <div className="label-meta" style={{ marginBottom: 12 }}>SUPPORT · ACCOUNT RESCUE</div>
          <h1 className="display" style={{ fontSize: 30, lineHeight: 1.15, marginBottom: 10 }}>
            <LifeBuoy size={22} style={{ display: "inline", marginRight: 8, marginBottom: -3 }} />
            Unlock a paid account
          </h1>
          <p className="text-[13px] mb-6" style={{ color: "var(--parapet-text-secondary)" }}>
            If you've subscribed via Stripe but your Parapet workspace is still
            blocked behind the paywall, paste your details below. We'll match
            your Stripe payment to your account and unlock it instantly.
          </p>

          <div
            className="p-3 mb-5 text-[12px]"
            style={{ border: "1px solid var(--parapet-grid)", background: "#fff", borderRadius: 4 }}
          >
            <strong>How to find your Stripe Customer ID:</strong>
            <ol style={{ paddingLeft: 18, marginTop: 6, lineHeight: 1.6 }}>
              <li>Open your Stripe receipt email (subject "Receipt from Parapet").</li>
              <li>Click "View receipt" — your customer ID is at the bottom of that page (starts with <code>cus_</code>).</li>
              <li>You'll also need a rescue token from <a className="underline" href="mailto:info@parapet.solutions">info@parapet.solutions</a>.</li>
            </ol>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="unstuck-form">
            <Field
              label="Your Parapet sign-up email"
              value={email}
              onChange={setEmail}
              placeholder="you@company.com"
              type="email"
              required
              testId="unstuck-email"
            />
            <Field
              label="Stripe Customer ID"
              value={customerId}
              onChange={setCustomerId}
              placeholder="cus_..."
              mono
              testId="unstuck-customer-id"
            />
            <Field
              label="Stripe Subscription ID (optional)"
              value={subscriptionId}
              onChange={setSubscriptionId}
              placeholder="sub_..."
              mono
              testId="unstuck-sub-id"
            />
            <Field
              label="Rescue token (from Parapet support)"
              value={token}
              onChange={setToken}
              placeholder="parapet-rescue-..."
              mono
              required
              testId="unstuck-token"
            />

            <button
              type="submit"
              disabled={submitting || !email || !token}
              className="w-full mt-2"
              style={{
                background: submitting || !email || !token ? "#888" : "#111",
                color: "#fff",
                border: 0,
                padding: "12px 16px",
                fontSize: 14,
                fontWeight: 600,
                cursor: submitting || !email || !token ? "not-allowed" : "pointer",
                borderRadius: 4,
              }}
              data-testid="unstuck-submit"
            >
              {submitting ? "Verifying with Stripe…" : "Unlock my account"}
            </button>
          </form>

          {result && (
            <div
              className="mt-5 p-3 text-[13px] flex items-start gap-2"
              style={{
                border: `1px solid ${result.ok ? "#0a6b2a" : "#7a0606"}`,
                background: result.ok ? "#E7F5EB" : "#FDECEC",
                borderRadius: 4,
              }}
              data-testid="unstuck-result"
            >
              {result.ok
                ? <CheckCircle2 size={18} style={{ color: "#0a6b2a", flexShrink: 0, marginTop: 1 }} />
                : <AlertCircle size={18} style={{ color: "#7a0606", flexShrink: 0, marginTop: 1 }} />}
              <div style={{ flex: 1 }}>
                {result.ok ? (
                  <>
                    <strong>Unlocked.</strong> Your workspace is now active. Sign back in
                    {" "}
                    <button onClick={() => navigate("/login")} className="underline" data-testid="unstuck-go-login">here</button>.
                  </>
                ) : (
                  <>
                    <strong>Couldn't unlock.</strong> {result.data?.detail}
                    <div className="text-[11px] mt-2" style={{ color: "var(--parapet-text-secondary)" }}>
                      Forward your Stripe receipt to <a className="underline" href="mailto:info@parapet.solutions">info@parapet.solutions</a> and we'll fix it manually.
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <footer
        style={{
          padding: "20px 40px",
          borderTop: "1px solid var(--parapet-grid)",
          fontSize: 12,
          color: "var(--parapet-text-secondary)",
          textAlign: "center",
        }}
      >
        <div>Stripe handles all card details. Parapet only stores Customer + Subscription IDs.</div>
        <div style={{ marginTop: 10, fontSize: 11, lineHeight: 1.5 }}>
          © {new Date().getFullYear()} Parapet. Parapet is a trading name of Linkup Ltd,
          a company registered in England and Wales under company number 17060828.
        </div>
      </footer>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", mono = false, required = false, testId }) {
  return (
    <label className="block">
      <div className="text-[11px] mb-1" style={{ color: "var(--parapet-text-secondary)", letterSpacing: "0.04em", textTransform: "uppercase" }}>
        {label}{required ? " *" : ""}
      </div>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full"
        style={{
          padding: "10px 12px",
          fontSize: 13,
          border: "1px solid var(--parapet-grid)",
          borderRadius: 4,
          background: "#fff",
          color: "#111",
          fontFamily: mono ? "ui-monospace,SFMono-Regular,Menlo,monospace" : "inherit",
        }}
        data-testid={testId}
      />
    </label>
  );
}
